import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import apiService from '../services/apiService';

const DeviceInviteSignup = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [invite, setInvite] = useState(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });

  const canSubmit = useMemo(() => {
    return !!formData.email && !!formData.password && !!formData.confirmPassword && !submitting;
  }, [formData, submitting]);

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const resp = await apiService.get(`/public/device-invites/${encodeURIComponent(token)}`);
        setInvite(resp);
      } catch (e) {
        setError(e?.message || 'Failed to load invite');
      } finally {
        setLoading(false);
      }
    };

    if (token) run();
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError(null);
      await apiService.post(`/public/device-invites/${encodeURIComponent(token)}/register`, {
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword
      });
      setSuccess(true);
    } catch (e2) {
      setError(e2?.message || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh'
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <WaterDropIcon color="primary" sx={{ fontSize: 60, mb: 2 }} />
            <Typography component="h1" variant="h5" fontWeight="bold" color="primary">
              Device Registration
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" mt={1} textAlign="center">
              Create your account and link it to the device.
            </Typography>
          </Box>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Account created successfully. You can now sign in.
            </Alert>
          )}

          {!loading && !success && invite?.device && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Device
              </Typography>
              <Typography variant="body1" fontWeight={700}>
                {invite.device.description || invite.device.serialNumber}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {invite.device.serialNumber}
              </Typography>
            </Box>
          )}

          {!loading && !success && invite?.device && (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                disabled={submitting}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="password"
                label="Password"
                name="password"
                type="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
                disabled={submitting}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="confirmPassword"
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                value={formData.confirmPassword}
                onChange={handleChange}
                disabled={submitting}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, py: 1.5 }}
                disabled={!canSubmit}
              >
                {submitting ? <CircularProgress size={24} /> : 'Create Account'}
              </Button>
            </Box>
          )}

          {!loading && (success || !!error) && (
            <Button
              fullWidth
              variant="text"
              sx={{ mt: 2 }}
              onClick={() => navigate('/login')}
            >
              Go to Login
            </Button>
          )}
        </Paper>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
          &copy; {new Date().getFullYear()} Thynk-H2O. All rights reserved.
        </Typography>
      </Box>
    </Container>
  );
};

export default DeviceInviteSignup;
